/** @jsx jsx */
import { EConsentMode, React, ReactRedux, appActions, getAppStore, hooks, jsx, utils, css } from 'jimu-core';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Switch } from 'jimu-ui';
import defaultMessages from '../../translations/default';
const SettingItem = (props) => {
    const { name, enable = false, description, onChange, disabled } = props;
    return (jsx("div", { className: 'cookie-settings-item d-flex flex-column' },
        jsx("div", { className: 'd-flex justify-content-between', onClick: () => {
                // Support a11y switch behavior
                onChange(!enable);
            } },
            jsx("h5", null, name),
            jsx(Switch, { checked: enable, disabled: disabled, onChange: (e) => { onChange(e.target.checked); } })),
        jsx("span", null, description)));
};
const getModalBodyStyle = () => {
    return css `
    .modal-body {
      .cookie-settings-item:not(:last-child) {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid var(--ref-palette-neutral-400);
      }
    }
  `;
};
const CookieSettingsWindow = (props) => {
    const { bannerUid, isEditable, onSave, toggle, builderEntry } = props;
    const translate = hooks.useTranslation(defaultMessages);
    const { dispatch } = getAppStore();
    const consentMode = ReactRedux.useSelector((state) => { var _a, _b; return (_b = (_a = state.appConfig) === null || _a === void 0 ? void 0 : _a.cookieBanner) === null || _b === void 0 ? void 0 : _b.consentMode; });
    const [enablePerformanceCookie, setEnablePerformanceCookie] = React.useState(false);
    const settingItemsJson = [
        {
            name: translate('essentialCookies'),
            enable: true,
            description: translate('essentialCookiesDesc'),
            onChange: () => null,
            disabled: true
        },
        {
            name: translate('performanceCookies'),
            enable: enablePerformanceCookie,
            description: translate('performanceCookiesDesc'),
            onChange: (enable) => {
                if (isEditable) {
                    return;
                }
                setEnablePerformanceCookie(enable);
            }
        }
    ];
    React.useEffect(() => {
        const COOKIE_CONFIRMATIONS_KEY = `${utils.getLocalStorageAppKey()}-cookie-confirmations`;
        if (utils.readLocalStorage(COOKIE_CONFIRMATIONS_KEY)) {
            const cookieConfirmations = JSON.parse(utils.readLocalStorage(COOKIE_CONFIRMATIONS_KEY));
            // Expand the cookie types here
            if (cookieConfirmations.performance !== undefined) {
                // Use the localStorage config if it is defined
                setEnablePerformanceCookie(cookieConfirmations.performance);
            }
        }
        else {
            // Turn on all the categories by default if it's optional mode
            setEnablePerformanceCookie(!!(!consentMode || consentMode === EConsentMode.Optional));
        }
    }, [consentMode]);
    const onToggle = React.useCallback(() => {
        if (isEditable) {
            return;
        }
        dispatch(appActions.OpenCookieSettingsWindow(false));
        toggle();
    }, [dispatch, isEditable, toggle]);
    const onSaveBtnClick = React.useCallback(() => {
        if (isEditable) {
            return;
        }
        let cookieConfirmations = null;
        const COOKIE_CONFIRMATIONS_KEY = `${utils.getLocalStorageAppKey()}-cookie-confirmations`;
        if (utils.readLocalStorage(COOKIE_CONFIRMATIONS_KEY)) {
            cookieConfirmations = JSON.parse(utils.readLocalStorage(COOKIE_CONFIRMATIONS_KEY));
            cookieConfirmations.performance = enablePerformanceCookie;
            cookieConfirmations.confirmTime = new Date().getTime();
            cookieConfirmations.uid = bannerUid;
        }
        else {
            // We only have one cookie type for now
            cookieConfirmations = {
                confirmTime: new Date().getTime(),
                uid: bannerUid,
                performance: enablePerformanceCookie
            };
        }
        // Do not save to the local storage if it is live-view
        if (!window.jimuConfig.isInBuilder) {
            utils.setLocalStorage(COOKIE_CONFIRMATIONS_KEY, JSON.stringify(cookieConfirmations));
        }
        else {
            builderEntry && builderEntry.appBuilderSync.publishCloseBannerToBuilder();
        }
        onSave && onSave();
        onToggle();
    }, [isEditable, onSave, onToggle, enablePerformanceCookie, bannerUid, builderEntry]);
    return (jsx(Modal, { centered: true, isOpen: true, toggle: onToggle, className: 'cookie-banner-setting-modal', css: getModalBodyStyle() },
        jsx(ModalHeader, { toggle: onToggle }, translate('cookieSettings')),
        jsx(ModalBody, { className: 'cookie-banner-setting-body' }, settingItemsJson.map((itemProps, index) => {
            return jsx(SettingItem, Object.assign({}, itemProps, { key: index }));
        })),
        jsx(ModalFooter, null,
            jsx(Button, { type: 'primary', onClick: onSaveBtnClick }, translate('saveChanges')))));
};
export default CookieSettingsWindow;
